
import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/base/WhatsAppButton';
import BackToTopButton from '../../components/base/BackToTopButton';
import CookieBanner from '../../components/base/CookieBanner';
import SEOHead from '../../components/seo/SEOHead';

export default function Home() {
  const [projectsCount, setProjectsCount] = useState(0);
  const [satisfactionCount, setSatisfactionCount] = useState(0);
  const [experienceCount, setExperienceCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            // Animate projects counter
            const projectsInterval = setInterval(() => {
              setProjectsCount(prev => {
                if (prev < 150) return prev + 5;
                clearInterval(projectsInterval);
                return 150;
              });
            }, 30);

            // Animate satisfaction counter
            const satisfactionInterval = setInterval(() => {
              setSatisfactionCount(prev => {
                if (prev < 98) return prev + 2;
                clearInterval(satisfactionInterval);
                return 98;
              });
            }, 40);

            // Animate experience counter
            const experienceInterval = setInterval(() => {
              setExperienceCount(prev => {
                if (prev < 15) return prev + 1;
                clearInterval(experienceInterval);
                return 15;
              });
            }, 80);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, [hasAnimated]);

  const seoSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "PintOLOT",
    "alternateName": "PintOLOT Servicios de Pintura",
    "description": "Servicios profesionales de pintura para particulares y empresas en Olot y la Garrotxa",
    "url": `${import.meta.env.VITE_SITE_URL}`,
    "telephone": "+34609936590",
    "email": "info@pintolot.com.es",
    "image": `${import.meta.env.VITE_SITE_URL}/og-image.jpg`,
    "logo": `${import.meta.env.VITE_SITE_URL}/logo.png`,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Olot",
      "addressRegion": "Girona",
      "postalCode": "17800",
      "addressCountry": "ES"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 42.1816,
      "longitude": 2.4829
    },
    "areaServed": [
      {
        "@type": "City",
        "name": "Olot"
      },
      {
        "@type": "AdministrativeArea",
        "name": "La Garrotxa"
      },
      {
        "@type": "AdministrativeArea",
        "name": "Girona"
      }
    ],
    "serviceType": [
      "Pintura interior",
      "Pintura exterior",
      "Pintura comercial",
      "Pintura industrial",
      "Lacados y barnizados"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Servicios de Pintura",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pintura Interior",
            "description": "Servicios de pintura interior para viviendas"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pintura Exterior",
            "description": "Pintura de fachadas y exteriores"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Pintura Comercial",
            "description": "Servicios de pintura para empresas y oficinas"
          }
        }
      ]
    },
    "priceRange": "€€",
    "openingHours": [
      "Mo-Fr 08:00-18:00",
      "Sa 09:00-14:00"
    ],
    "paymentAccepted": "Cash, Credit Card, Bank Transfer",
    "currenciesAccepted": "EUR",
    "foundingDate": "2008",
    "yearsInOperation": "15",
    "slogan": "Pintura profesional con garantía de calidad",
    "sameAs": [
      "https://wa.me/34609936590"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": [
      {
        "@type": "Review",
        "author": {
          "@type": "Person",
          "name": "María González"
        },
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5"
        },
        "reviewBody": "Excelente trabajo de pintura interior. Muy profesionales y puntuales."
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="PintOLOT - Pintor Profesional Olot y Garrotxa |"
        description="Empresa de pintores en Olot con +15 años experiencia. Servicios para particulares y empresas en Garrotxa y Girona. Presupuesto gratuito."
        keywords="pintura Olot, pintores Garrotxa, servicios pintura Girona, pintura profesional, empresa pintura, presupuesto pintura, pintor Olot, pintores, presupuesto,"
        canonical={`${import.meta.env.VITE_SITE_URL}/`}
        ogTitle="PintOLOT - Pintor Profesional Olot y Garrotxa"
        ogDescription="Empresa de pintores en Olot con +15 años experiencia. Servicios para particulares y empresas en Garrotxa y Girona."
        schema={seoSchema}
      />
      <Header />
      <main>
        {/* Hero Section with Video Background */}
        <section className="relative min-h-screen flex items-center overflow-hidden">
          {/* Video Background */}
          <div className="absolute inset-0 w-full h-full">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              poster="https://public.readdy.ai/ai/img_res/1609489ca2385cbdf2bfd2d4fb1f4b5e.jpg"
            >
              <source
                src="https://readdy.ai/api/search-image?query=professional%20painters%20team%20working%20on%20modern%20house%20exterior%20painting%2C%20time%20lapse%20style%20video%2C%20clean%20residential%20building%2C%20workers%20in%20uniforms%20with%20professional%20equipment%2C%20natural%20daylight%2C%20high%20quality%20cinematography&width=1920&height=1080&seq=hero-video&orientation=landscape"
                type="video/mp4"
              />
              {/* Fallback image if video doesn't load */}
              <img
                src="https://readdy.ai/api/search-image?query=professional%20painters%20working%20on%20house%20exterior%20walls%20with%20brushes%20and%20paint%20rollers%2C%20modern%20residential%20building%2C%20clean%20white%20background%2C%20professional%20lighting%2C%20high%20quality%20photography%2C%20workers%20wearing%20clean%20uniforms&width=1920&height=1080&seq=hero-painting-fallback&orientation=landscape"
                alt="Pintores profesionales trabajando"
                className="w-full h-full object-cover"
              />
            </video>
          </div>

          {/* Animated Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30 animate-pulse"></div>

          {/* Floating Elements Animation */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 left-10 w-4 h-4 bg-white/20 rounded-full animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}></div>
            <div className="absolute top-40 right-20 w-3 h-3 bg-[#51b038]/30 rounded-full animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}></div>
            <div className="absolute bottom-40 left-20 w-5 h-5 bg-white/15 rounded-full animate-bounce" style={{ animationDelay: '2s', animationDuration: '5s' }}></div>
            <div className="absolute bottom-20 right-10 w-2 h-2 bg-[#51b038]/40 rounded-full animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '3.5s' }}></div>
          </div>

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-10">
            <div className="text-white max-w-2xl">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight animate-fade-in-up">
                Pintor profesional en Olot y la Garrotxa
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-200 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                Transformamos espacios con calidad profesional. Pintores especialistas en pintura para particulares y empresas en Girona y provincia.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
                <a 
                  href="/contacto" 
                  className="bg-[#51b038] hover:bg-[#7a806a] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 cursor-pointer whitespace-nowrap text-center transform hover:scale-105 hover:shadow-xl"
                >
                  Solicitar Presupuesto
                </a>
                <a 
                  href="tel:609936590" 
                  className="border-2 border-white text-white hover:bg-white hover:text-[#51b038] px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 cursor-pointer whitespace-nowrap text-center transform hover:scale-105 hover:shadow-xl"
                >
                  Llamar Ahora
                </a>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce text-center">
            <div className="flex flex-col items-center">
              <span className="text-sm mb-2 opacity-80">Descubre más</span>
              <i className="ri-arrow-down-line text-2xl"></i>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nuestros servicios de pintura
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Ofrecemos servicios completos de pintura profesional para todo tipo de proyectos en Olot, Girona y toda la comarca de la Garrotxa.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden cursor-pointer">
                {/* Overlay effect */}
                <div className="absolute inset-0 bg-[#51b038]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-home-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-4 group-hover:text-[#51b038] transition-colors duration-300">Pintura para particulares</h3>
                  <p className="text-gray-600 text-center mb-6">
                    Pintamos tu hogar con los mejores materiales. Interiores, exteriores, habitaciones, salones y toda tu vivienda.
                  </p>
                  <div className="text-center">
                    <a href="/servicios" className="text-[#51b038] hover:text-[#7a806a] font-semibold cursor-pointer group-hover:underline transition-all duration-300">
                      Ver más detalles →
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden cursor-pointer">
                {/* Overlay effect */}
                <div className="absolute inset-0 bg-[#51b038]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-building-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-4 group-hover:text-[#51b038] transition-colors duration-300">Pintura para empresas</h3>
                  <p className="text-gray-600 text-center mb-6">
                    Servicios profesionales para oficinas, locales comerciales, naves industriales y todo tipo de espacios empresariales.
                  </p>
                  <div className="text-center">
                    <a href="/servicios" className="text-[#51b038] hover:text-[#7a806a] font-semibold cursor-pointer group-hover:underline transition-all duration-300">
                      Ver más detalles →
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden cursor-pointer">
                {/* Overlay effect */}
                <div className="absolute inset-0 bg-[#51b038]/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-[#51b038] rounded-lg flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-palette-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-center mb-4 group-hover:text-[#51b038] transition-colors duration-300">Servicios especializados</h3>
                  <p className="text-gray-600 text-center mb-6">
                    Lacados, barnizados, tratamientos especiales y técnicas decorativas para proyectos únicos y personalizados.
                  </p>
                  <div className="text-center">
                    <a href="/servicios" className="text-[#51b038] hover:text-[#7a806a] font-semibold cursor-pointer group-hover:underline transition-all duration-300">
                      Ver más detalles →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                ¿Por qué elegir PINTOLOT en Olot?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Somos la empresa de referencia en servicios de pintura en Olot y toda la comarca de la Garrotxa. 
                Nuestro compromiso con la calidad y la satisfacción del cliente nos convierte en la mejor opción.
              </p>

              <div className="space-y-8">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-[#51b038] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-shield-check-line text-white text-xl"></i>
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-semibold mb-2">Más de 15 años de experiencia</h3>
                    <p className="text-gray-600">
                      Profesionales cualificados con amplia experiencia en proyectos de pintura en Girona y provincia.
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-[#51b038] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-award-line text-white text-xl"></i>
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-semibold mb-2">Materiales de primera calidad</h3>
                    <p className="text-gray-600">
                      Utilizamos únicamente pinturas y materiales de las mejores marcas para garantizar durabilidad y acabados perfectos.
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-12 h-12 bg-[#51b038] rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div className="text-left">
                    <h3 className="text-xl font-semibold mb-2">Puntualidad garantizada</h3>
                    <p className="text-gray-600">
                      Cumplimos con los plazos acordados y respetamos tu tiempo. Planificación detallada de cada proyecto.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 bg-[#51b038]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Zonas de servicio
              </h2>
              <p className="text-xl text-green-100 max-w-3xl mx-auto">
                Ofrecemos nuestros servicios de pintura profesional en Olot y toda la comarca de la Garrotxa, así como en toda la provincia de Girona.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                'Olot', 'Girona', 'Figueres', 'Blanes',
                'Sant Feliu de Guíxols', 'Lloret de Mar', 'Ripoll', 'Puigcerdà',
                'La Bisbal d\'Empordà', 'Palafrugell', 'Roses', 'Cadaqués'
              ].map((city) => (
                <div key={city} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                  <i className="ri-map-pin-line text-2xl text-white mb-2"></i>
                  <h3 className="text-white font-semibold">{city}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Lo que dicen nuestros clientes
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                La satisfacción de nuestros clientes es nuestra mejor carta de presentación. Descubre por qué confían en PINTOLOT para sus proyectos de pintura.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-[#51b038] rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">MR</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">María Rodríguez</h4>
                    <p className="text-gray-600 text-sm">Particular - Olot</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 italic">
                  "Excelente trabajo en la pintura de mi casa. Muy profesionales, puntuales y el resultado superó mis expectativas. Recomiendo PINTOLOT sin dudarlo."
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-[#51b038] rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">JM</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Josep Martínez</h4>
                    <p className="text-gray-600 text-sm">Empresa - Girona</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 italic">
                  "Pintaron nuestras oficinas en tiempo récord y con una calidad impecable. El equipo es muy profesional y respeta los horarios de trabajo. Muy satisfecho."
                </p>
              </div>

              <div className="bg-gray-50 p-8 rounded-lg shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-[#51b038] rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">AG</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Anna García</h4>
                    <p className="text-gray-600 text-sm">Particular - La Garrotxa</p>
                  </div>
                </div>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 italic">
                  "Servicio excepcional desde el primer contacto. Presupuesto claro, trabajo limpio y acabados perfectos. Sin duda volveré a contar con ellos."
                </p>
              </div>
            </div>

            <div className="text-center mt-12" ref={statsRef}>
              <div className="bg-[#51b038] text-white p-6 rounded-lg inline-block">
                <div className="flex items-center justify-center space-x-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold transition-all duration-500 ease-out">
                      {projectsCount}+
                    </div>
                    <div className="text-sm text-green-100">Proyectos completados</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold transition-all duration-500 ease-out">
                      {satisfactionCount}%
                    </div>
                    <div className="text-sm text-green-100">Clientes satisfechos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold transition-all duration-500 ease-out">
                      {experienceCount}+
                    </div>
                    <div className="text-sm text-green-100">Años de experiencia</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              ¿Necesitas un presupuesto de pintura?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Contacta con nosotros para recibir un presupuesto gratuito y sin compromiso. 
              Te asesoramos en tu proyecto de pintura en Olot y la Garrotxa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contacto" 
                className="bg-[#51b038] hover-bg-[#7a806a] text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Solicitar presupuesto gratuito
              </a>
              <a 
                href="tel:609936590" 
                className="border-2 border-[#51b038] text-[#51b038] hover:bg-[#51b038] hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                609 93 65 90
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTopButton />
      <CookieBanner />
    </>
  );
}
